import { Box, Button, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { LocationIcon } from "@/assets";
import { ReportDownloadButton } from "@/components/reportDownloadButton";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";

type ValuationState = CurrentValuationState | ValuationReportState;

interface AnalysisSummaryCardProps {
  valuationState: ValuationState;
  onEditObjectData?: () => void;
  onGenerateReport?: () => void;
  onGenerateWordDocument?: () => void;
  isGenerateReportDisabled?: boolean;
  isGenerateReportLoading?: boolean;
  isLoading?: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  root: {
    display: "grid",
    gap: theme.analysisLayout.panelGap,
    alignContent: "start",
    backgroundColor: "background.paper",
    padding: theme.analysisLayout.panelPadding,
  },
  addressSection: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  addressIcon: {
    color: "text.primary",
    fontSize: "20px",
    flexShrink: 0,
  },
  addressTextRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "12px",
    rowGap: "4px",
    minWidth: 0,
  },
  addressPrimary: {
    fontWeight: 600,
  },
  actions: {
    display: "grid",
    gap: "14px",
  },
});

const formatAddress = (address: ValuationState["address"]): { primary: string; secondary: string } => {
  if (!address) {
    return { primary: "-", secondary: "" };
  }

  const primary = [address.street, address.house_number].filter(Boolean).join(" ").trim();
  const secondary = [address.post_code, address.town].filter(Boolean).join(" ").trim();

  return {
    primary: secondary && primary ? `${primary},` : primary || "-",
    secondary,
  };
};

export const AnalysisSummaryCard = ({
  valuationState,
  onEditObjectData,
  onGenerateReport,
  onGenerateWordDocument,
  isGenerateReportDisabled = false,
  isGenerateReportLoading = false,
}: AnalysisSummaryCardProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("ValuationAnalysisPage");

  const address = formatAddress(valuationState.address);

  return (
    <Box sx={styles.root}>
      <Box sx={styles.addressSection}>
        <LocationIcon sx={{ ...styles.addressIcon, fontSize: theme.analysisLayout.titleFontSize }} />
        <Box sx={styles.addressTextRow}>
          <Typography sx={styles.addressPrimary}>{address.primary}</Typography>
          {address.secondary ? <Typography>{address.secondary}</Typography> : null}
        </Box>
      </Box>
      <Box sx={styles.actions}>
        <ReportDownloadButton
          label={t("generateReport")}
          onPdfAction={onGenerateReport}
          onWordDocumentAction={onGenerateWordDocument}
          isDisabled={isGenerateReportDisabled || (!onGenerateReport && !onGenerateWordDocument)}
          isLoading={isGenerateReportLoading}
          dataCy="summary-card-create-report"
        />
        <Button variant="outlined" color="primary" onClick={onEditObjectData} disabled={!onEditObjectData}>
          {t("editObjectData")}
        </Button>
      </Box>
    </Box>
  );
};
